import { useState, useRef, useEffect } from 'react';

// Generic checkbox multi-select pill dropdown with "Select all" — brand
// purple styling, no Overview-specific logic, so Platforms/Products/Forecast
// can reuse it later.
//
// `values` is the literal set of checked options — an empty array means "no
// filter applied" (backend treats it as "all platforms"), NOT "everything is
// individually checked". Rendering an empty selection as all-checked was a
// real bug: the first click out of that state toggled against a value the
// user couldn't see (values was actually []), producing confusing results
// that looked like "the filter doesn't work". Checkbox state must always be
// the literal values.includes(option) — never inferred.
export default function MultiSelectDropdown({ values, options, onChange, placeholder = 'Select' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const noneSelected = values.length === 0;
    const allSelected = values.length === options.length && options.length > 0;
    const label = noneSelected ? `All ${placeholder}` : allSelected ? `All ${placeholder}` : `${values.length} of ${options.length} ${placeholder}`;

    function toggle(v) {
        if (values.includes(v)) onChange(values.filter(x => x !== v));
        else onChange([...values, v]);
    }

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                    borderRadius: 999, border: `1px solid ${open ? PURPLE_600 : '#E4DCF0'}`, background: '#fff',
                    fontSize: 13, fontWeight: 600, color: '#3D3452', cursor: 'pointer',
                    boxShadow: open ? '0 0 0 3px rgba(110,88,166,0.12)' : 'none', transition: 'box-shadow 0.15s, border-color 0.15s',
                }}
            >
                {label}
                <span style={{ fontSize: 10, color: '#8C82A0', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20, minWidth: 220, maxHeight: 300, overflowY: 'auto',
                    background: '#fff', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', border: '1px solid #EFEAF6',
                    padding: 6,
                }}>
                    <div style={{ display: 'flex', gap: 6, padding: '2px 4px 8px', borderBottom: '1px solid #F1EDF9', marginBottom: 4 }}>
                        <button
                            type="button"
                            onClick={() => onChange(options.map(o => o.value))}
                            disabled={allSelected}
                            style={pillButtonStyle(allSelected)}
                        >
                            Select all
                        </button>
                        <button
                            type="button"
                            onClick={() => onChange([])}
                            disabled={noneSelected}
                            style={pillButtonStyle(noneSelected)}
                        >
                            Clear
                        </button>
                    </div>
                    {options.map(o => {
                        const checked = values.includes(o.value);
                        return (
                            <label
                                key={o.value}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                                    color: '#3D3452', background: checked ? '#F7F4FC' : 'transparent',
                                }}
                            >
                                <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} style={{ accentColor: PURPLE_600, width: 15, height: 15 }} />
                                {o.label}
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const PURPLE_600 = '#6E58A6';

function pillButtonStyle(disabled) {
    return {
        flex: 1, padding: '6px 8px', borderRadius: 999, border: '1px solid #E4DCF0',
        background: disabled ? '#F7F4FC' : '#fff', color: disabled ? '#B8ADC9' : PURPLE_600,
        fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
    };
}
